import { useRef } from 'react'

import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Col, Grid, Row } from '~/components/atoms/Grid'
import type { TeamMemberProfileProps } from '~/components/atoms/TeamMemberProfile/TeamMemberProfile'
import TeamMemberProfile from '~/components/atoms/TeamMemberProfile/TeamMemberProfile'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './TeamProfileList.module.css'

export interface TeamProfileListProps {
  list: TeamMemberProfileProps[]
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  title: string
}

export default function TeamProfileList({
  list,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  title,
  testID,
}: TeamProfileListProps) {
  const profileWrap = useRef<HTMLDivElement[]>([])
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)

  const renderDesktopView = () => {
    return (
      <Grid>
        <Row>
          {list.map((member: TeamMemberProfileProps) => {
            return (
              <Col
                variant="col-1/2--sm col-1/4--md col-1/4--hg"
                key={member._id}
                cssClass={styles.item}
              >
                <div
                  ref={ref => {
                    if (ref) profileWrap.current.push(ref as HTMLDivElement)
                  }}
                >
                  <TeamMemberProfile
                    {...member}
                    testID={testID && `profile-${testID}`}
                  />
                </div>
              </Col>
            )
          })}
        </Row>
      </Grid>
    )
  }

  const renderMobileView = () => {
    return (
      <DynamicSlider
        slidesPerView={1.15}
        slidesPerViewMed={1}
        slidesPerViewLrg={3}
        slidesPerViewHuge={4}
        overflowVisible={true}
        title={''}
        spaceBetween={0}
        hideArrowsMobile
        wrapperClass={styles.sliderWrapperClass}
      >
        {list.map((member: TeamMemberProfileProps, i: number) => (
          <TeamMemberProfile
            {...member}
            key={`mobile-team-member-${i}`}
            testID={testID && `profile-${testID}`}
          />
        ))}
      </DynamicSlider>
    )
  }

  const renderTeamList = () => {
    if (!isMobile) return renderDesktopView()

    return renderMobileView()
  }

  return (
    <Fixture
      verticalPadding={verticalPadding}
      theme={theme}
      noPaddingBottom={noPaddingBottom}
    >
      <div className={styles.base} data-testid={testID}>
        <Container className={isMobile ? 'noPaddingLeftRight' : ''}>
          <div className={styles.sectionTitleSectionContainer}>
            <div className={styles.sectionTitleSection}>
              <Text
                size={TextSizes.primaryTitleLrg}
                className={styles.sectionTitle}
              >
                {title}
              </Text>
            </div>
          </div>
          {renderTeamList()}
        </Container>
      </div>
    </Fixture>
  )
}
